import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { Select, Divider } from 'antd';
import {
  LBNodeData,
  type TextTemplateChunk,
} from '@/proto/logiblocks/logiblocks';
import { VariableDesc } from '@/proto/common';
import { getCollectVariableType } from '@/common/collectVariableType';
import styles from '../NodeCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { TemplatedTextArea } from '@/pages/Logiblocks/variables/TemplatedTextArea/TemplatedTextArea';
import {
  customerLegEnabled,
  FieldErrorLabel,
  FieldLabel,
  useShipmentContactFilter,
  withLogiblocksContactRoles,
} from '../shared';
import { SmsActionFields } from './SmsActionNodeInfo';
import useToken from '@/common/helper/useToken';
import { GetUsersByTeamIds, GetAiAgentDropdownsOptions } from '@/apis/settings';
import { TeamWithUsers, AdditionalRecipientOption } from '@/apis/types';
import CheckboxMultiSelect from '@/components/CheckboxMultiSelect';
import ToggleSwitch from '@/components/ToggleSwitch';
import { BrackedAddIcon } from '@/assets/icons/icons';
import { displayNameToSnakeCase } from '@/pages/Logiblocks/variables/variableUtils';
import { USER_VARIABLES_FIELD } from '@/pages/Logiblocks/validation';

interface ContactOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CollectOption {
  id: string | number;
  name: string;
  variable_name?: string;
  is_archived?: boolean;
  variable_type?: string | null;
  sample_value?: string | null;
}

export const SmsActionCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const recipientError = validationErrors?.find(
    (e) => e.field === SmsActionFields.RECIPIENT
  );
  const messageTemplateError = validationErrors?.find(
    (e) => e.field === SmsActionFields.MESSAGE_TEMPLATE
  );
  const userVariablesError = validationErrors?.find(
    (e) => e.field === USER_VARIABLES_FIELD
  );
  const { token } = useToken();

  const rawSmsData = nodeData.sms_action;
  const currentSmsData = useMemo(
    () => ({
      phone: rawSmsData?.phone ?? '',
      message_chunks: rawSmsData?.message_chunks ?? [],
      contact_id: rawSmsData?.contact_id ?? '',
      wait_for_response: rawSmsData?.wait_for_response ?? false,
      update_coc: rawSmsData?.update_coc ?? false,
    }),
    [rawSmsData]
  );

  const [contactId, setContactId] = useState(currentSmsData.contact_id);
  const [waitForResponse, setWaitForResponse] = useState(
    currentSmsData.wait_for_response
  );
  const [updateCoc, setUpdateCoc] = useState(currentSmsData.update_coc);

  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [collectOptions, setCollectOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [collectVariableTypeMap, setCollectVariableTypeMap] = useState<
    Record<string, string | null | undefined>
  >({});
  const [collectSampleValueMap, setCollectSampleValueMap] = useState<
    Record<string, string | null | undefined>
  >({});

  const triggerPickerRef = useRef<(() => void) | null>(null);

  const userVariables: VariableDesc[] = nodeData.user_variables ?? [];

  // Derive the multi-select value from userVariables (match by display name)
  const selectedCollectValues = userVariables.map((uv) => uv.display_name);

  const notifyChange = useCallback(
    (updates: Partial<typeof currentSmsData>) => {
      const updatedData: Partial<LBNodeData> = {
        sms_action: {
          ...currentSmsData,
          ...updates,
        },
      };
      onChange(updatedData);
    },
    [currentSmsData, onChange]
  );

  useEffect(() => {
    const smsData = nodeData.sms_action || {
      phone: '',
      contact_id: '',
      wait_for_response: false,
      update_coc: false,
    };
    setContactId(smsData.contact_id || '');
    setWaitForResponse(smsData.wait_for_response || false);
    setUpdateCoc(smsData.update_coc || false);
  }, [nodeData]);

  useEffect(() => {
    if (!token) return;

    setLoadingContacts(true);
    GetUsersByTeamIds(token, [])
      .then((res) => {
        const data = (res?.data?.data?.data || {}) as {
          additional?: AdditionalRecipientOption[];
          teams_users?: TeamWithUsers[];
        };
        const roleOptions: ContactOption[] = [];
        const additional = data.additional || [];
        additional.forEach((item) => {
          if (item.enabled !== false) {
            roleOptions.push({
              value: item.key,
              label: item.label,
              disabled: !item.enabled,
            });
          }
        });
        const contactOptions: ContactOption[] = withLogiblocksContactRoles(
          roleOptions,
          { customerLeg: customerLegEnabled() }
        );

        const teamsUsers = data.teams_users || [];
        teamsUsers.forEach((team) => {
          const users = team.users || [];
          users.forEach((user) => {
            contactOptions.push({
              value: `${team.id}::${user.id}`,
              label: user.name || user.email || user.id,
            });
          });
        });

        setContacts(contactOptions);
      })
      .catch((error) => {
        console.error('Error fetching contacts:', error);
        setContacts([]);
      })
      .finally(() => {
        setLoadingContacts(false);
      });
  }, [token]);

  useEffect(() => {
    if (!token) return;

    GetAiAgentDropdownsOptions(token)
      .then((res) => {
        const data = (res?.data?.data?.data || {}) as {
          collect?: CollectOption[];
        };
        const collectData = data.collect || [];

        const active = collectData.filter((c) => !c.is_archived);
        setCollectOptions(
          active.map((c) => ({
            value: c.variable_name || c.name,
            label: c.name,
          }))
        );
        const variableTypeMap: Record<string, string | null | undefined> = {};
        const sampleValueMap: Record<string, string | null | undefined> = {};
        const activeByKey = new Map<string, CollectOption>();
        active.forEach((c) => {
          const key = c.variable_name || c.name;
          variableTypeMap[key] = c.variable_type;
          sampleValueMap[key] = c.sample_value;
          activeByKey.set(key, c);
        });
        setCollectVariableTypeMap(variableTypeMap);
        setCollectSampleValueMap(sampleValueMap);

        // Self-heal previously-saved user_variables whose type/sample_value have
        // drifted from the current catalog entry (e.g. saved before this node
        // correctly mapped boolean/number collect fields, or before sample
        // values existed) — mirrors the resync AICallActionCustomization does
        // when its agent's collect fields load.
        const currentUserVars = nodeData.user_variables ?? [];
        if (currentUserVars.length > 0) {
          const reconciled = currentUserVars.map((uv) => {
            const collect = activeByKey.get(uv.name);
            if (!collect) return uv;
            const type = getCollectVariableType(collect);
            const sampleValue = collect.sample_value ?? '';
            return VariableDesc.fromPartial({
              ...uv,
              type,
              sample_value: sampleValue,
            });
          });
          const changed = reconciled.some(
            (uv, i) =>
              uv.type !== currentUserVars[i]?.type ||
              uv.sample_value !== currentUserVars[i]?.sample_value
          );
          if (changed) {
            onChange({ user_variables: reconciled });
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching collect options:', error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleContactIdChange = (newContactId: string) => {
    setContactId(newContactId);
    notifyChange({ contact_id: newContactId });
  };

  const { hasUpstreamShipment, visibleContacts } = useShipmentContactFilter(
    nodeId,
    contacts,
    contactId,
    handleContactIdChange
  );

  const handleMessageChange = (newChunks: TextTemplateChunk[]) => {
    notifyChange({ message_chunks: newChunks });
  };

  const handleWaitForResponseChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const checked = e.target.checked;
    setWaitForResponse(checked);
    if (!checked) {
      onChange({
        sms_action: {
          ...currentSmsData,
          wait_for_response: false,
        },
        user_variables: [],
      });
    } else {
      notifyChange({ wait_for_response: checked });
    }
  };

  const handleCollectVariableNamesChange = (values: string[]) => {
    onChange({
      user_variables: values.map((value) => {
        const type = getCollectVariableType({
          variable_type: collectVariableTypeMap[value],
        });
        return VariableDesc.fromPartial({
          name: displayNameToSnakeCase(value),
          display_name: value,
          type,
          sample_value: collectSampleValueMap[value] ?? '',
        });
      }),
    });
  };

  const handleUpdateCocChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setUpdateCoc(checked);
    notifyChange({ update_coc: checked });
  };

  return (
    <>
      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Recipient
        </FieldLabel>
        <Select
          value={contactId || undefined}
          onChange={handleContactIdChange}
          placeholder={
            loadingContacts ? 'Loading contacts...' : 'Select a contact'
          }
          disabled={isReadOnly || loadingContacts}
          style={{ width: '100%' }}
          className='agentSelect'
          showSearch
          optionFilterProp='label'
        >
          {visibleContacts.map((contact) => (
            <Select.Option
              key={contact.value}
              value={contact.value}
              label={contact.label}
              disabled={contact.disabled}
            >
              {contact.label}
            </Select.Option>
          ))}
        </Select>
        <FieldErrorLabel error={recipientError} />
      </div>

      <div className={styles.customizationSection}>
        <div className={`${styles.headerRow} ${styles.headerRowSpaced}`}>
          <FieldLabel
            required
            className={styles.sectionLabel}
            style={{ marginBottom: 0 }}
          >
            Message Template
          </FieldLabel>
          {!isReadOnly && (
            <button
              type='button'
              className={styles.iconButton}
              onClick={() => triggerPickerRef.current?.()}
              aria-label='Insert variable'
            >
              <BrackedAddIcon />
            </button>
          )}
        </div>
        <TemplatedTextArea
          nodeId={nodeId}
          chunks={currentSmsData.message_chunks}
          onChange={handleMessageChange}
          placeholder='Enter your SMS message template...'
          disabled={isReadOnly}
          onTriggerPickerRef={(fn) => {
            triggerPickerRef.current = fn;
          }}
        />
        <FieldErrorLabel error={messageTemplateError} />
      </div>

      <Divider className='agentFormDivider' style={{ margin: '16px 0' }} />

      <div className={styles.customizationSection}>
        <div className={styles.headerRow}>
          <label className={`${styles.sectionLabel} ${styles.labelNoMargin}`}>
            Wait for Response
          </label>
          <ToggleSwitch
            checked={waitForResponse}
            onChange={handleWaitForResponseChange}
            disabled={isReadOnly}
          />
        </div>
      </div>

      {waitForResponse && (
        <div className={styles.customizationSection}>
          <label className={styles.sectionLabel}>Information to Collect</label>
          <CheckboxMultiSelect
            titleText='Select information to collect'
            options={collectOptions}
            value={selectedCollectValues}
            onChange={(values) =>
              handleCollectVariableNamesChange(values.map(String))
            }
            className='agentSelect agentSelect--darkText'
            disabled={isReadOnly}
            showTagCloseIcon={true}
            background='#5E5E5E'
            color='#fff'
          />
          <FieldErrorLabel error={userVariablesError} />
        </div>
      )}

      <Divider className='agentFormDivider' style={{ margin: '16px 0' }} />
      <div className={styles.customizationSection}>
        <div className={styles.headerRow}>
          <label className={`${styles.sectionLabel} ${styles.labelNoMargin}`}>
            Update Chain of Custody
          </label>
          <ToggleSwitch
            checked={updateCoc}
            onChange={handleUpdateCocChange}
            disabled={isReadOnly || !hasUpstreamShipment}
          />
        </div>
      </div>
    </>
  );
};
